import { useState, type FC } from 'react'
import {
    Modal,
    Stack,
    Group,
    Text,
    Button,
    SegmentedControl,
    Switch,
    TextInput,
    PasswordInput,
    Divider,
    Badge,
    Alert,
} from '@mantine/core'
import { IconInfoCircle, IconTrash } from '@tabler/icons-react'
import {
    destinationRoutes,
    findDestination,
    requiresRouteChoice,
    routeConfigSteps,
    MARKETPLACE_DELIVERY_LABEL,
    MARKETPLACE_PROVIDER_LABEL,
} from '@/lib/marketplaceDestinations'
import type {
    ConfigSlotRef,
    ConfigStep,
    DestinationRoute,
} from '@/lib/marketplaceDestinations'
import { useSegmentStubs } from '../queries'
import { SCHEDULE_LABEL } from '../labels'
import type { Destination, Distribution, DistributionSchedule } from '../types'

const routeKey = (route: DestinationRoute): string =>
    route.kind === 'direct' ? 'direct' : `via:${route.via}`

// Buyer-facing route label — the generic delivery alias, never the real
// marketplace name (see MARKETPLACE_DELIVERY_LABEL).
const routeLabel = (route: DestinationRoute): string =>
    route.kind === 'direct' ? 'Direct' : MARKETPLACE_DELIVERY_LABEL[route.via]

// Identify a config slot for read/write.
const stepRef = (step: ConfigStep): ConfigSlotRef => {
    if (step.kind === 'marketplace-connection') {
        return { kind: 'marketplace', provider: step.provider }
    }
    if (step.kind === 'direct-connection') {
        return { kind: 'direct', destination: step.destination }
    }
    return { kind: 'destination', destination: step.destination, via: step.via }
}

const slotKey = (step: ConfigStep): string => {
    const ref = stepRef(step)
    return ref.kind === 'marketplace'
        ? `m:${ref.provider}`
        : ref.kind === 'direct'
          ? `d:${ref.destination}`
          : `s:${ref.destination}:${ref.via}`
}

const stepTitle = (step: ConfigStep, platform: string): string => {
    if (step.kind === 'marketplace-connection') {
        return `${MARKETPLACE_PROVIDER_LABEL[step.provider]} account`
    }
    if (step.kind === 'direct-connection') return `${platform} connection`
    return `${platform} setup`
}

export interface DistributionConfigModalProps {
    distribution: Distribution
    destination?: Destination
    opened: boolean
    onClose: () => void
}

// Edit one distribution config: route (when there's a choice), schedule, pause,
// and the lazily-collected connection / per-destination setup fields. Saving
// merges partial config — half-finished setup is allowed and resurfaces as
// needs-config on the Distributions page.
export const DistributionConfigModal: FC<DistributionConfigModalProps> = ({
    distribution,
    destination,
    opened,
    onClose,
}) => {
    const {
        savedConfig,
        setDistribution,
        removeDistribution,
        saveDestinationConfig,
    } = useSegmentStubs()

    const platform = destination?.platform ?? distribution.destinationId
    const marketplaceDest = destination
        ? findDestination(destination.platform)
        : undefined
    const routes = marketplaceDest ? destinationRoutes(marketplaceDest) : []
    const mustChooseRoute = marketplaceDest
        ? requiresRouteChoice(marketplaceDest)
        : false

    const [schedule, setSchedule] = useState<DistributionSchedule>(
        distribution.schedule ?? 'weekly'
    )
    const [paused, setPaused] = useState(distribution.paused ?? false)
    const [selectedRouteKey, setSelectedRouteKey] = useState<string>(() => {
        // Defaults to the first carrier; the user can re-route multi-carrier
        // destinations via the control below.
        const initial = distribution.route ?? routes[0]
        return initial ? routeKey(initial) : ''
    })
    // Field values across every candidate route, seeded from what's saved.
    const [values, setValues] = useState<Record<string, string>>(() => {
        if (!marketplaceDest) return {}
        const seeded: Record<string, string> = {}
        for (const route of routes) {
            for (const step of routeConfigSteps(
                marketplaceDest,
                route,
                savedConfig
            )) {
                const sk = slotKey(step)
                for (const field of step.fields) {
                    seeded[`${sk}.${field.key}`] = step.values[field.key] ?? ''
                }
            }
        }
        return seeded
    })

    const selectedRoute =
        routes.find((r) => routeKey(r) === selectedRouteKey) ?? routes[0]
    const steps =
        marketplaceDest && selectedRoute
            ? routeConfigSteps(marketplaceDest, selectedRoute, savedConfig)
            : []

    const handleSave = () => {
        // Persist (merge) the entered config for the selected route's slots.
        for (const step of steps) {
            const sk = slotKey(step)
            const slotValues: Record<string, string> = {}
            for (const field of step.fields) {
                const value = (values[`${sk}.${field.key}`] ?? '').trim()
                if (value) slotValues[field.key] = value
            }
            if (Object.keys(slotValues).length > 0) {
                saveDestinationConfig(stepRef(step), slotValues)
            }
        }
        setDistribution(distribution.id, {
            schedule,
            paused,
            route: selectedRoute,
        })
        onClose()
    }

    const handleRemove = () => {
        removeDistribution(distribution.id)
        onClose()
    }

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={<Text fw={600}>Configure {platform}</Text>}
            centered
            size='lg'
        >
            <Stack gap='lg'>
                {/* Route. Labels are the generic delivery aliases, never the real
                    marketplace names. */}
                {routes.length > 0 &&
                    (mustChooseRoute ? (
                        <div>
                            <Text size='sm' fw={500} mb={6}>
                                Delivery route
                            </Text>
                            <SegmentedControl
                                value={selectedRouteKey}
                                onChange={setSelectedRouteKey}
                                data={routes.map((r) => ({
                                    value: routeKey(r),
                                    label: routeLabel(r),
                                }))}
                            />
                            <Text size='xs' c='dimmed' mt={6}>
                                {platform} can be delivered more than one way —
                                choose a route.
                            </Text>
                        </div>
                    ) : (
                        selectedRoute && (
                            <Group gap='xs'>
                                <Text size='sm' fw={500}>
                                    Delivery route:
                                </Text>
                                <Badge variant='light'>
                                    {selectedRoute.kind === 'direct'
                                        ? 'Direct integration'
                                        : `Via ${routeLabel(selectedRoute)}`}
                                </Badge>
                            </Group>
                        )
                    ))}

                {/* Schedule */}
                <div>
                    <Text size='sm' fw={500} mb={6}>
                        Schedule
                    </Text>
                    <SegmentedControl
                        value={schedule}
                        onChange={(v) => setSchedule(v as DistributionSchedule)}
                        data={[
                            { value: 'once', label: SCHEDULE_LABEL.once },
                            { value: 'weekly', label: SCHEDULE_LABEL.weekly },
                        ]}
                    />
                </div>

                {/* Pause */}
                <Switch
                    checked={paused}
                    onChange={(e) => setPaused(e.currentTarget.checked)}
                    label='Pause this delivery'
                    description='Paused deliveries keep their config but stop running on schedule.'
                />

                {/* Connection + setup fields */}
                {steps.length > 0 ? (
                    <Stack gap='md'>
                        <Divider label='Setup' labelPosition='left' />
                        {steps.map((step) => {
                            const sk = slotKey(step)
                            return (
                                <Stack gap='xs' key={sk}>
                                    <Text
                                        size='xs'
                                        c='dimmed'
                                        tt='uppercase'
                                        fw={600}
                                    >
                                        {stepTitle(step, platform)}
                                    </Text>
                                    {step.fields.map((field) => {
                                        const ck = `${sk}.${field.key}`
                                        const Comp =
                                            field.type === 'secret'
                                                ? PasswordInput
                                                : TextInput
                                        return (
                                            <Comp
                                                key={ck}
                                                label={field.label}
                                                placeholder={field.placeholder}
                                                value={values[ck] ?? ''}
                                                onChange={(e) => {
                                                    const value =
                                                        e.currentTarget.value
                                                    setValues((prev) => ({
                                                        ...prev,
                                                        [ck]: value,
                                                    }))
                                                }}
                                            />
                                        )
                                    })}
                                </Stack>
                            )
                        })}
                    </Stack>
                ) : (
                    <Alert
                        color='gray'
                        icon={<IconInfoCircle size={18} />}
                        title='No additional setup'
                    >
                        This destination doesn&apos;t require connection setup.
                    </Alert>
                )}

                <Group justify='space-between' mt='xs'>
                    <Button
                        variant='subtle'
                        color='error'
                        leftSection={<IconTrash size={16} />}
                        onClick={handleRemove}
                    >
                        Remove delivery
                    </Button>
                    <Group gap='xs'>
                        <Button variant='default' onClick={onClose}>
                            Cancel
                        </Button>
                        <Button onClick={handleSave}>Save</Button>
                    </Group>
                </Group>
            </Stack>
        </Modal>
    )
}
